import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Presentation, Trash2, Edit, Copy, Sparkles } from "lucide-react";
import { SlideEditor } from "./SlideEditor";
import { useLocalization } from '@/hooks/useLocalization';

export interface Slide {
  title: string;
  bullets: string[];
  notes?: string;
}

interface PresentationTemplate {
  id: string;
  name: string;
  deck_type: string;
  slides: Slide[];
  source: "manual" | "ai-generated";
  project_id: string | null;
  is_default: boolean;
  created_at: string;
}

const deckTypes = [
  { value: "status-update", label: "Status Update" },
  { value: "kickoff", label: "Project Kickoff" },
  { value: "stakeholder-review", label: "Stakeholder Review" },
  { value: "risk-review", label: "Risk Review" },
  { value: "custom", label: "Custom" },
];

const starterDecks: Omit<PresentationTemplate, "id" | "created_at" | "project_id">[] = [
  {
    name: "Weekly Status Update",
    deck_type: "status-update",
    source: "manual",
    is_default: true,
    slides: [
      { title: "{{project_name}} — Status Update", bullets: ["{{date}}", "Presented by {{author}}"] },
      { title: "Executive Summary", bullets: ["{{summary_point_1}}", "{{summary_point_2}}"] },
      { title: "Progress & Milestones", bullets: ["{{milestone_1}}", "{{milestone_2}}"] },
      { title: "Budget Status", bullets: ["Allocated: {{budget_allocated}}", "Spent: {{budget_spent}}"] },
      { title: "Risks & Blockers", bullets: ["{{risk_1}}"] },
      { title: "Next Steps", bullets: ["{{next_step_1}}", "{{next_step_2}}"] },
    ],
  },
  {
    name: "Project Kickoff",
    deck_type: "kickoff",
    source: "manual",
    is_default: true,
    slides: [
      { title: "{{project_name}} — Kickoff", bullets: ["{{date}}"] },
      { title: "Project Overview & Goals", bullets: ["{{goal_1}}", "{{goal_2}}"] },
      { title: "Scope", bullets: ["{{scope_item_1}}"] },
      { title: "Timeline & Milestones", bullets: ["{{milestone_1}}"] },
      { title: "Team & Roles", bullets: ["{{team_member_1}} — {{role_1}}"] },
      { title: "Success Criteria", bullets: ["{{criterion_1}}"] },
    ],
  },
  {
    name: "Stakeholder Review",
    deck_type: "stakeholder-review",
    source: "manual",
    is_default: true,
    slides: [
      { title: "{{project_name}} — Stakeholder Review", bullets: ["{{date}}"] },
      { title: "Project Health Summary", bullets: ["Status: {{health_status}}"] },
      { title: "Key Achievements", bullets: ["{{achievement_1}}"] },
      { title: "Critical Decisions Needed", bullets: ["{{decision_1}}"] },
      { title: "Next 30 Days", bullets: ["{{priority_1}}"] },
    ],
  },
];

export function PresentationTemplates() {
  const { tx, t } = useLocalization();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [selectedDeckType, setSelectedDeckType] = useState<string>("status-update");
  const [editingDeck, setEditingDeck] = useState<PresentationTemplate | null>(null);

  const { data: decks = [], isLoading } = useQuery({
    queryKey: ["presentation-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("presentation_templates")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as PresentationTemplate[];
    },
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["projects-for-presentation"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects_sync")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("generate-presentation", {
        body: { deckType: selectedDeckType, projectId: selectedProjectId },
      });
      if (error) {
        let message = error.message;
        try {
          const body = await error.context?.json();
          if (body?.error) message = body.error;
        } catch {
          // response body wasn't JSON (or already consumed) — fall back to error.message
        }
        throw new Error(message);
      }
      if (data.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["presentation-templates"] });
      setIsGenerateOpen(false);
      toast({ title: t('Presentation generated'), description: t('{0} is ready to review and export.', { 0: data.name }) });
      setEditingDeck({
        id: data.id,
        name: data.name,
        deck_type: selectedDeckType,
        slides: data.slides,
        source: "ai-generated",
        project_id: selectedProjectId,
        is_default: false,
        created_at: new Date().toISOString(),
      });
    },
    onError: (error: Error) => {
      toast({ title: t('Generation failed'), description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("presentation_templates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["presentation-templates"] });
      toast({ title: t('Presentation deleted') });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (deck: PresentationTemplate) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { error } = await supabase.from("presentation_templates").insert({
        user_id: user.id,
        name: `${deck.name} (Copy)`,
        deck_type: deck.deck_type,
        slides: deck.slides as unknown as never,
        source: "manual",
        is_default: false,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["presentation-templates"] });
      toast({ title: t('Presentation duplicated') });
    },
  });

  const addStarterDeck = async (deck: typeof starterDecks[0]) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { error } = await supabase.from("presentation_templates").insert({
      user_id: user.id,
      name: deck.name,
      deck_type: deck.deck_type,
      slides: deck.slides as unknown as never,
      source: "manual",
      is_default: true,
    });
    if (error) {
      toast({ title: t('Failed to add template'), description: error.message, variant: "destructive" });
    } else {
      queryClient.invalidateQueries({ queryKey: ["presentation-templates"] });
      toast({ title: t('Template added'), description: t('{0} has been added.', { 0: deck.name }) });
    }
  };

  if (editingDeck) {
    return (
      <SlideEditor
        deck={editingDeck}
        onClose={() => {
          setEditingDeck(null);
          queryClient.invalidateQueries({ queryKey: ["presentation-templates"] });
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">{t('Your Presentations')}</h2>
          <p className="text-sm text-muted-foreground">
            
            {t('Generate a branded, meeting-ready deck from real project data, or start from a template')}
          </p>
        </div>
        <Dialog open={isGenerateOpen} onOpenChange={setIsGenerateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Sparkles className="h-4 w-4 me-2" />
              
              {t('Generate from Project')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('Generate Presentation')}</DialogTitle>
              <DialogDescription>
                
                {t('AI drafts slides from your project\'s real data. You can edit everything before exporting.')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('Project')}</Label>
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('Select a project')} />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {projects.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    
                    {t('No projects synced yet. Connect an integration first.')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>{t('Deck Type')}</Label>
                <Select value={selectedDeckType} onValueChange={setSelectedDeckType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {deckTypes.filter((t) => t.value !== "custom").map((t) => (
                      <SelectItem key={t.value} value={t.value}>{tx(t.label)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsGenerateOpen(false)}>{t('Cancel')}</Button>
                <Button
                  onClick={() => generateMutation.mutate()}
                  disabled={!selectedProjectId || generateMutation.isPending}
                >
                  {generateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('Generate')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {decks.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('Get Started')}</CardTitle>
            <CardDescription>{t('Add a starter template or generate one from a project above')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              {starterDecks.map((deck, index) => (
                <Card key={index} className="cursor-pointer hover:border-primary transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Presentation className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base">{deck.name}</CardTitle>
                    </div>
                    <Badge variant="secondary">{deck.slides.length}  {t('slides')}</Badge>
                  </CardHeader>
                  <CardContent>
                    <Button size="sm" className="w-full" onClick={() => addStarterDeck(deck)}>
                      <Plus className="h-4 w-4 me-2" />
                      
                      {t('Add Template')}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {decks.map((deck) => (
            <Card key={deck.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Presentation className="h-5 w-5 text-primary" />
                    <CardTitle className="text-base">{deck.name}</CardTitle>
                  </div>
                  <Badge variant={deck.source === "ai-generated" ? "default" : "secondary"}>
                    {deck.source === "ai-generated" ? t('AI-drafted') : deck.deck_type}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">{deck.slides.length}  {t('slides')}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditingDeck(deck)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => duplicateMutation.mutate(deck)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-destructive hover:text-destructive"
                    onClick={() => deleteMutation.mutate(deck.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {decks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('Add More Templates')}</CardTitle>
            <CardDescription>{t('Choose from starter templates')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {starterDecks.map((deck, index) => (
                <Button key={index} variant="outline" size="sm" onClick={() => addStarterDeck(deck)}>
                  <Plus className="h-4 w-4 me-2" />
                  {deck.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
